import { ReactElement, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import DropdownControl from '../controls/DropdownControl';
import { getAdminClients, updateAdminClient } from '../services/dashboard-service';
import { Client, Option } from '../constants/telosair-interfaces';
import { useData } from '../context/data.context';

/**
 * ClientChange component for rendering a dropdown to show all clients.
 * @returns A React element representing the ClientChange component.
 */
export default function ClientChange(): ReactElement {
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const { currentUser, clientData, loading } = useData();

  /**
   * Handles the update of the client.
   * @param clientId - The id of the client.
   */
  const handleUpdateClient = async (clientId: number): Promise<void> => {
    try {
      if (currentUser) {
        const payload = {
          id: currentUser.id,
          firstName: currentUser.firstName,
          lastName: currentUser.lastName,
          role: currentUser.role,
          status: currentUser.status,
          userName: currentUser.userName,
          clientId
        };
        await updateAdminClient(payload, currentUser.id);
        localStorage.removeItem('applied_filters');
        toast.success('Client updated successfully, page will reload in 2 seconds');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        toast.error('Failed to update client');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      toast.error('Failed to update client');
    }
  };

  /**
   * Handles the selection of a client.
   * @param client - The client object containing the id and name.
   * @param client.id - The id of the client.
   * @param client.name - The name of the client.
   */
  const handleClientSelect = (client: Option<number>): void => {
    const selectedDropdownClient = clients.find((c) => c.id === client.id);
    setSelectedClient(selectedDropdownClient || null);
    handleUpdateClient(client.id);
  };

  const clientOptions = clients
    .filter((client) => !client.is_deleted)
    .map((client) => ({
      name: client.clientName,
      id: client.id
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    // eslint-disable-next-line jsdoc/require-jsdoc
    const fetchClients = async (): Promise<void> => {
      try {
        const res = await getAdminClients();
        setClients(res.data);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    };
    fetchClients();
  }, []);

  useEffect(() => {
    if (!loading && clientData) {
      setSelectedClient(clientData);
    }
  }, [clientData, loading]);

  return (
    <DropdownControl
      controlPlaceholder={selectedClient?.clientName || 'Change Client'}
      options={clientOptions}
      onSelect={handleClientSelect}
    />
  );
}
